import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Undo2, HeartHandshake, Filter, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { FullSpinner, Empty } from "@/components/States";
import { toast } from "sonner";

const STATUS = { pending: ["Pending", "text-amber-700 bg-amber-50"], accepted: ["Accepted", "text-emerald-700 bg-emerald-50"], declined: ["Declined", "text-rose-700 bg-rose-50"], withdrawn: ["Withdrawn", "text-slate-500 bg-slate-100"], ignored: ["Ignored", "text-slate-500 bg-slate-100"] };

export default function Interests() {
  const nav = useNavigate();
  const [tab, setTab] = useState("matching");
  const [data, setData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});

  const load = () => api.get("/interests").then(({ data }) => setData(data));
  useEffect(() => { load(); api.get("/contact-filters").then(({ data }) => setFilters(data.filters || {})); }, []);
  if (!data) return <FullSpinner />;

  const act = async (id, action) => { try { await api.post(`/interests/${action}/${id}`); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveFilters = async () => { try { await api.put("/contact-filters", { filters }); toast.success("Contact filters saved"); load(); } catch (e) { toast.error(apiError(e)); } };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const received = data.received;
  const matching = received.filter((i) => !i.filtered);
  const filtered = received.filter((i) => i.filtered);
  const items = tab === "sent" ? data.sent : tab === "filtered" ? filtered : matching;

  return (
    <div className="space-y-5" data-testid="interests-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold text-[#58111A]">Interests</h1>
        <button onClick={() => setShowFilters(!showFilters)} className="btn-outline text-sm" data-testid="contact-filters-toggle"><Filter className="w-4 h-4" /> Contact Filters</button>
      </div>

      {showFilters && (
        <div className="mj-card p-5 grid sm:grid-cols-3 gap-3 animate-fadeup" data-testid="contact-filters-panel">
          <div><label className="mj-label">Age Min</label><input className="mj-input" type="number" value={filters.age_min || ""} onChange={(e) => setF("age_min", e.target.value)} /></div>
          <div><label className="mj-label">Age Max</label><input className="mj-input" type="number" value={filters.age_max || ""} onChange={(e) => setF("age_max", e.target.value)} /></div>
          <div><label className="mj-label">City</label><input className="mj-input" value={filters.cities || ""} onChange={(e) => setF("cities", e.target.value)} /></div>
          <div><label className="mj-label">Religion</label><input className="mj-input" value={filters.religion || ""} onChange={(e) => setF("religion", e.target.value)} /></div>
          <div><label className="mj-label">Mother Tongue</label><input className="mj-input" value={filters.mother_tongue || ""} onChange={(e) => setF("mother_tongue", e.target.value)} /></div>
          <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-[#261A1C]"><input type="checkbox" checked={!!filters.verified_only} onChange={(e) => setF("verified_only", e.target.checked)} className="w-4 h-4 accent-[#781826]" /> Verified only</label></div>
          <div className="sm:col-span-3"><button onClick={saveFilters} className="btn-primary" data-testid="save-contact-filters"><Save className="w-4 h-4" /> Save Filters</button>
            <span className="text-xs text-[#9E7B1B] ml-3">Non-matching interests move to "Filtered Out" — never auto-rejected.</span></div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto hide-scrollbar">
        {[["matching", `Matching (${matching.filter((i) => i.status === "pending").length})`], ["filtered", `Filtered Out (${filtered.length})`], ["sent", `Sent (${data.sent.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`interest-tab-${k}`} className={`shrink-0 text-sm font-medium px-4 py-2 rounded-full border ${tab === k ? "bg-[#781826] text-white border-[#781826]" : "bg-white border-[#E8DFD1] text-[#605255]"}`}>{l}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <Empty icon={HeartHandshake} title={tab === "filtered" ? "Nothing filtered out" : tab === "sent" ? "No interests sent" : "No interests yet"} message={tab === "sent" ? "Explore your recommended matches and send an Interest." : tab === "filtered" ? "Interests that don't match your contact filters will appear here." : "When someone expresses interest, it'll appear here."} action={<a href="/app/matches" className="btn-primary">Explore Matches</a>} />
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const pr = it.profile;
            const [label, cls] = STATUS[it.status] || ["", ""];
            const other = tab === "sent" ? it.to_user : it.from_user;
            return (
              <div key={it.id} className="mj-card p-4 flex items-center gap-4" data-testid={`interest-${it.id}`}>
                <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 cursor-pointer" onClick={() => nav(`/app/profile/${other}`)}>
                  <Avatar photos={pr?.photos} name={pr?.full_name} size={9999} className="w-full h-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg font-semibold text-[#261A1C]">{pr?.full_name}{pr?.age ? `, ${pr.age}` : ""}</div>
                  <div className="text-xs text-[#605255]">{[pr?.city, pr?.occupation].filter(Boolean).join(" · ")}</div>
                  <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                  {it.filtered && it.filter_reason && <div className="text-[11px] text-[#B45309] mt-1">⚑ {it.filter_reason}</div>}
                </div>
                <div className="flex gap-2">
                  {tab !== "sent" && it.status === "pending" && (
                    <>
                      <button onClick={() => act(it.id, "accept")} data-testid={`accept-${it.id}`} className="btn-primary text-sm py-1.5 px-3"><Check className="w-4 h-4" /></button>
                      <button onClick={() => act(it.id, "decline")} data-testid={`decline-${it.id}`} className="btn-outline text-sm py-1.5 px-3"><X className="w-4 h-4" /></button>
                    </>
                  )}
                  {tab === "sent" && it.status === "pending" && <button onClick={() => act(it.id, "withdraw")} className="btn-outline text-sm py-1.5 px-3"><Undo2 className="w-4 h-4" /> Withdraw</button>}
                  {it.status === "accepted" && <button onClick={() => nav(`/app/messages/${other}`)} className="btn-primary text-sm py-1.5 px-3">Chat</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
